/**
 * Non-hook authenticated fetch helper.
 *
 * Use this from places where React hooks are illegal (registries,
 * imperative event handlers wired up outside the React tree, top-level
 * shell utilities). For any code reachable from a render path, prefer
 * `useApiClient` / `useApiQuery` / `useApiMutation`.
 *
 * Token + tenant are read from `localStorage` using the same keys the
 * `AuthProvider` / `TenantProvider` write to:
 *   - `auth_token`  (JWT or `dev-token-*` in development)
 *   - `tenant_id`   (UUID of the active tenant)
 *
 * The dev-token bypass on the gateway recognises both bearer header and
 * SignalR query param, so the helper just forwards the bearer header.
 */

// Explicit `.ts` extension: this module is reachable from the token-refresh
// contract test via tokenRefresh's lazy `import('./apiFetch.ts')`, and the
// node --test native ESM loader (unlike the bundler) will not resolve an
// extensionless relative import. Matches the convention the other
// test-reachable modules in this package already follow.
import { buildTimeoutSignal } from './abortSignal.ts';

const DEFAULT_BASE = '/api/v1';

/**
 * Resolve the API base URL. Honors a `globalThis.TYNDO_API_URL` override:
 *   - Absolute (`http(s)://...`) → used as-is. Lets the shell talk directly
 *     to the gateway (e.g. `http://localhost:5050/api/v1`) instead of going
 *     through the rspack-dev-server proxy, so requests show up on the
 *     real gateway host in browser dev tools.
 *   - Relative (`/api/v1`) → prepended with `window.location.origin`, which
 *     keeps the request same-origin and routes through whatever proxy the
 *     dev server has configured.
 *
 * Exported so every API/SignalR helper in this package shares one
 * resolution path.
 */
export const resolveApiBaseUrl = (): string => {
  const override = (typeof globalThis !== 'undefined' &&
    (globalThis as typeof globalThis & Record<string, unknown>).TYNDO_API_URL) as
    | string
    | undefined;
  const path = override ?? DEFAULT_BASE;
  if (/^https?:\/\//i.test(path)) return path;
  if (typeof window === 'undefined') return path;
  return window.location.origin + path;
};

const getBaseUrl = resolveApiBaseUrl;

const normalize = (url: string) => url.replace(/^\/api\/v\d+/, '');

const readToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('auth_token');
  } catch {
    return null;
  }
};

const readTenantId = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('tenant_id');
  } catch {
    return null;
  }
};

export interface ApiFetchInit extends Omit<RequestInit, 'body'> {
  /** Plain JSON body — auto-stringified, content-type set to application/json. */
  json?: unknown;
  /** Raw body — passed through untouched. */
  body?: BodyInit | null;
  /**
   * Per-request client timeout in milliseconds (default 30 s). A hung
   * gateway otherwise leaves the fetch pending forever with no abort.
   * Composed with any caller-supplied `signal`.
   */
  timeoutMs?: number;
}

/** Default client timeout for the non-hook fetch helper. */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Build a full URL for the given gateway endpoint, preserving any
 * `/api/vN` prefix the caller already supplied.
 */
export function buildApiUrl(endpoint: string): string {
  if (endpoint.startsWith('http')) return endpoint;
  return `${getBaseUrl()}${normalize(endpoint)}`;
}

/**
 * Authenticated `fetch` against the Tyndo gateway. Returns the raw
 * `Response` so callers can decide whether to parse JSON, stream a blob,
 * or check status codes themselves.
 *
 * Adds `Authorization: Bearer <auth_token>` and `X-Tenant-Id: <tenant_id>`
 * automatically when those values are present in `localStorage`.
 */
export async function apiFetch(endpoint: string, init: ApiFetchInit = {}): Promise<Response> {
  const url = buildApiUrl(endpoint);

  const headers: Record<string, string> = {};
  if (init.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((v, k) => {
        headers[k] = v;
      });
    } else if (Array.isArray(init.headers)) {
      for (const [k, v] of init.headers as [string, string][]) headers[k] = v;
    } else {
      Object.assign(headers, init.headers as Record<string, string>);
    }
  }

  let body: BodyInit | null | undefined = init.body ?? null;
  if (init.json !== undefined) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
    body = JSON.stringify(init.json);
  }

  const token = readToken();
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const tenantId = readTenantId();
  if (tenantId && !headers['X-Tenant-Id']) {
    headers['X-Tenant-Id'] = tenantId;
  }

  const { timeoutMs, signal, ...restInit } = init;
  return fetch(url, {
    ...restInit,
    method: init.method ?? (init.json !== undefined ? 'POST' : 'GET'),
    headers,
    body,
    signal: buildTimeoutSignal(timeoutMs ?? DEFAULT_TIMEOUT_MS, signal),
  });
}

/**
 * Convenience wrapper that parses JSON and throws on non-2xx with a
 * useful error.
 */
export async function apiFetchJson<T = unknown>(endpoint: string, init: ApiFetchInit = {}): Promise<T> {
  const res = await apiFetch(endpoint, init);
  if (!res.ok) {
    let message = res.statusText;
    try {
      const data: unknown = await res.json();
      message = (data as { message?: string })?.message ?? message;
    } catch {
      /* ignore */
    }
    const err = new Error(message) as Error & { statusCode?: number };
    err.statusCode = res.status;
    throw err;
  }
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    return (await res.json()) as T;
  }
  return undefined as T;
}
